import { create } from 'zustand';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

interface ProgressEvent {
  type: string;
  data: any;
  timestamp: string;
}

interface Session {
  id: string;
  session_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  messages?: Message[];
}

interface ChatState {
  sessions: Session[];
  currentSession: Session | null;
  currentMessages: Message[];
  progressEvents: ProgressEvent[];
  isLoadingSessions: boolean;
  isLoadingMessages: boolean;
  isSending: boolean;
  isStreaming: boolean;
  error: string | null;

  // Actions
  fetchSessions: () => Promise<void>;
  fetchSessionMessages: (sessionId: string) => Promise<void>;
  createNewSession: (title?: string) => Promise<string>;
  sendMessage: (sessionId: string | null, message: string, title?: string) => Promise<void>;
  setCurrentSession: (session: Session | null) => void;
  clearCurrentMessages: () => void;
  deleteSession: (sessionId: string) => Promise<void>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<void>;
  clearError: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://literature.aiexecutiveconsulting.com/api';

export const useChatStore = create<ChatState>((set, get) => ({
  sessions: [],
  currentSession: null,
  currentMessages: [],
  progressEvents: [],
  isLoadingSessions: false,
  isLoadingMessages: false,
  isSending: false,
  isStreaming: false,
  error: null,

  fetchSessions: async () => {
    set({ isLoadingSessions: true, error: null });
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE_URL}/sessions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch sessions');

      const data = await response.json();
      if (data.success && data.data) {
        set({ sessions: data.data.sessions || [] });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch sessions' });
    } finally {
      set({ isLoadingSessions: false });
    }
  },

  fetchSessionMessages: async (sessionId: string) => {
    set({ isLoadingMessages: true, error: null });
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/messages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch messages');

      const data = await response.json();
      if (data.success && data.data) {
        set({ currentMessages: data.data.messages || [] });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch messages' });
    } finally {
      set({ isLoadingMessages: false });
    }
  },

  createNewSession: async (title?: string) => {
    // When creating a new session via chat, we don't need to call this separately
    // The chat endpoint will create a session if session_id is null
    const newSession: Session = {
      id: `temp-${Date.now()}`,
      session_id: '',
      title: title || `New Chat - ${new Date().toLocaleString()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    set({ currentSession: newSession, currentMessages: [] });
    return newSession.id;
  },

  sendMessage: async (sessionId: string | null, message: string, title?: string) => {
    set({ isSending: true, isStreaming: true, error: null, progressEvents: [] });

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Not authenticated');

      // Add user message to current messages immediately
      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      };

      set((state) => ({
        currentMessages: [...state.currentMessages, userMessage],
      }));

      // Use fetch with streaming support
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: message,
          title: title,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      // Read the stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let assistantMessageId = `asst-${Date.now()}`;

      if (reader) {
        // Add placeholder for assistant message
        set((state) => ({
          currentMessages: [
            ...state.currentMessages,
            {
              id: assistantMessageId,
              role: 'assistant',
              content: '',
              timestamp: new Date().toISOString(),
            },
          ],
        }));

        let currentEvent = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                set({ isStreaming: false });
                break;
              }

              // Handle error messages from backend
              if (data.startsWith('[ERROR:')) {
                const errorMsg = data.slice(8, -1);
                set({ error: errorMsg, isStreaming: false });
                break;
              }

              // Handle progress events
              if (currentEvent && currentEvent !== '') {
                try {
                  const eventData = JSON.parse(data);
                  const progressEvent: ProgressEvent = {
                    type: currentEvent,
                    data: eventData,
                    timestamp: new Date().toISOString(),
                  };

                  set((state) => ({
                    progressEvents: [...state.progressEvents, progressEvent],
                  }));

                  currentEvent = ''; // Reset after processing
                } catch (e) {
                  // If parsing fails, treat as regular message content
                  assistantMessage += data;

                  set((state) => ({
                    currentMessages: state.currentMessages.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: assistantMessage }
                        : msg
                    ),
                  }));
                }
              } else {
                // Regular message content
                assistantMessage += data;

                set((state) => ({
                  currentMessages: state.currentMessages.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, content: assistantMessage }
                      : msg
                  ),
                }));
              }
            }
          }
        }
      }

      set({ isSending: false, isStreaming: false });

      // Refresh sessions list to get updated session info
      get().fetchSessions();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to send message',
        isSending: false,
        isStreaming: false,
      });
      throw error;
    }
  },

  setCurrentSession: (session: Session | null) => {
    set({ currentSession: session });
  },

  clearCurrentMessages: () => {
    set({ currentMessages: [] });
  },

  deleteSession: async (sessionId: string) => {
    // Note: Backend doesn't have delete endpoint yet, but we can add it
    set((state) => ({
      sessions: state.sessions.filter((s) => s.session_id !== sessionId),
    }));
  },

  updateSessionTitle: async (sessionId: string, title: string) => {
    // Note: Backend doesn't have update endpoint yet
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.session_id === sessionId ? { ...s, title } : s
      ),
    }));
  },

  clearError: () => set({ error: null }),
}));
